import {
  mdiFileDocumentOutline,
  mdiFileExcel,
  mdiFileImageOutline,
  mdiFileOutline,
  mdiFilePowerpoint,
  mdiFileWord,
} from '@mdi/js';
import * as exifr from 'exifr/dist/full.legacy.umd.js';


const ROTATE_INCR = Math.PI / 2; // 90 degree in radian
// See http://sylvana.net/jpegcrop/exif_orientation.html
const ORIENTATION_ROTATION = [undefined, 0, 0, 2, 2, 1, 1, -1, -1];

/**
 * @typedef {Object} FileIconInfo
 * @property {String} icon - Icon SVG data ; Can be pass as SVG path
 * @property {String} color - Icon color ; Can be pass as CSS color
 */
const FILE_ICON_INFO = {
  // Type
  image: { icon: mdiFileImageOutline, color: '#62b45b' },
  text: { icon: mdiFileDocumentOutline, color: '#1a73e8' },
  // Subtype
  'application/pdf': { icon: mdiFileOutline, color: '#ea4335' },
  // Word / Open Document Text
  'application/msword': { icon: mdiFileWord, color: '#185abd' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    icon: mdiFileWord,
    color: '#185abd',
  },
  'application/vnd.oasis.opendocument.text': { icon: mdiFileWord, color: '#185abd' },
  // Excel / Open Document Spreadsheet
  'application/vnd.ms-excel': { icon: mdiFileExcel, color: '#107c41' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: mdiFileExcel, color: '#107c41' },
  'application/vnd.oasis.opendocument.spreadsheet': { icon: mdiFileExcel, color: '#107c41' },
  // Powerpoint / Open Document Presentation
  'application/vnd.ms-powerpoint': { icon: mdiFilePowerpoint, color: '#c43e1c' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    icon: mdiFilePowerpoint, color: '#c43e1c',
  },
  'application/vnd.oasis.opendocument.presentation': { icon: mdiFilePowerpoint, color: '#c43e1c' },
};

// PDF file type
export const FILE_TYPE_PDF = 'application/pdf';
// DOC file type
export const FILE_TYPE_DOC = 'application/msword';
// DOCX file type
export const FILE_TYPE_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
// ODT file type
export const FILE_TYPE_ODT = 'application/vnd.oasis.opendocument.text';
// XLS file type
export const FILE_TYPE_XLS = 'application/vnd.ms-excel';
// XLSX file type
export const FILE_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
// ODS file type
export const FILE_TYPE_ODS = 'application/vnd.oasis.opendocument.spreadsheet';
// PPT file type
export const FILE_TYPE_PPT = 'application/vnd.ms-powerpoint';
// PPTX file type
export const FILE_TYPE_PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
// PPT file type
export const FILE_TYPE_ODP = 'application/vnd.oasis.opendocument.presentation';
/**
 * File mime types accepted by the system
 * Set according to $FILE_TYPES from server/files/func.php
 */
export const FILE_TYPE_ACCEPTED = {
  FILE_TYPE_PDF: '.pdf',
  FILE_TYPE_DOC: '.doc',
  FILE_TYPE_DOCX: '.docx',
  FILE_TYPE_ODT: '.odt',
  FILE_TYPE_XLS: '.xls',
  FILE_TYPE_XLSX: '.xlsx',
  FILE_TYPE_ODS: '.ods',
  FILE_TYPE_PPT: '.ppt',
  FILE_TYPE_PPTX: '.pptx',
  FILE_TYPE_ODP: '.odp',
  'image/bmp': '.bmp',
  'image/gif': '.gif',
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': '.png',
};
/*
 * Regex to test if MIME type is an image
 */
export const RE_IMAGE_TYPE = /^image\//;

/*
 * Get icon info about a file type
 * @param {String} mimeType - MIME type of the file
 * @return {FileIconInfo} Info about the file type
 */
export function getFileIconInfo(mimeType = '') {
  const defaultInfo = { icon: mdiFileOutline, color: '#1a73e8' };
  const type = mimeType.split('/')[0];
  return Object.assign(defaultInfo, FILE_ICON_INFO[type], FILE_ICON_INFO[mimeType]);
}

/**
 * Rotate a canvas image
 * @param {HTMLCanvasElement} canvas - Canvas having content to rotate
 * @param {Number} rotateFactor - Number of rotation by 90 degree (by ROTATE_INCR constant)
 */
export function rotateCanvasImage(canvas, rotateFactor = 1) {
  rotateFactor %= 4;
  if (!(canvas instanceof HTMLCanvasElement) || rotateFactor === 0) {
    return;
  }
  const ctx = canvas.getContext('2d');
  // Save image in a temporary canvas
  const tmpCanvas = document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d');
  tmpCanvas.width = canvas.width;
  tmpCanvas.height = canvas.height;
  tmpCtx.drawImage(canvas, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  // Swap width and height if rotated by 90/270 degree
  if (rotateFactor % 2 !== 0) {
    const tmp = canvas.width;
    canvas.width = canvas.height;
    canvas.height = tmp;
  }
  // Draw rotated image
  ctx.save();
  const translateX = tmpCanvas.width / 2;
  const translateY = tmpCanvas.height / 2;
  const angle = ROTATE_INCR * rotateFactor;
  ctx.translate(
    Math.abs(translateX * Math.cos(angle) + translateY * Math.sin(angle)),
    Math.abs(translateY * Math.cos(angle) + translateX * Math.sin(angle)),
  );
  ctx.rotate(angle);
  ctx.translate(-translateX, -translateY);
  ctx.drawImage(tmpCanvas, 0, 0);
  ctx.restore();
}

/*
 * Rotate image according to EXIF orientation (if any)
 * @param {Blob} file - Image file to handle
 * @return {Promise} promise with new Blob as a result
 */
export function handlePictureOrientation(blob) {
  return new Promise((resolve, reject) => {
    exifr.parse(blob, { pick: ['Orientation'], translateValues: false })
      .then((output) => {
        if (!output || !output.Orientation) {
          resolve(blob);
          return;
        }
        const canvas = document.createElement('canvas');
        const image = new Image();
        const url = URL.createObjectURL(blob);
        image.onload = () => {
          URL.revokeObjectURL(url);
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(image, 0, 0);
          rotateCanvasImage(canvas, ORIENTATION_ROTATION[output.Orientation]);
          canvas.toBlob((newBlob) => {
            resolve(newBlob);
          }, blob.type);
        };
        image.onerror = reject;
        image.src = url;
      })
      .catch((error) => {
        console.error(error);
        resolve(blob);
      });
  });
}

/*
 * Restrict image dimension according to maximum given
 * @param {Blob} file - Image file to handle
 * @param {Number} [maxWidth] - Maximum width of the resulted image
 * @param {Number} [maxHeight] - Maximum height of the resulted image
 * @return {Promise} promise with new Blob as a result
 */
export function handlePictureDimension(blob, maxWidth, maxHeight) {
  return new Promise((resolve, reject) => {
    if (
      (blob.type && !RE_IMAGE_TYPE.test(blob.type))
      || !(maxWidth > 0 || maxHeight > 0)
    ) {
      resolve(blob);
      return;
    }
    const image = new Image();
    const url = URL.createObjectURL(blob);
    image.onload = () => {
      URL.revokeObjectURL(url);
      let ratio = 1;
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (maxWidth > 0 && width > 0) {
        ratio = Math.min(ratio, maxWidth / width);
      }
      if (maxHeight > 0 && height > 0) {
        ratio = Math.min(ratio, maxHeight / height);
      }
      if (ratio >= 1) {
        // Height and width within limits ; No resize needed
        resolve(blob);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.height = height * ratio;
      canvas.width = width * ratio;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((newBlob) => {
        resolve(newBlob);
      }, blob.type);
    };
    image.onerror = reject;
    image.src = url;
  });
}

export function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (!reader.error) {
        const dataUrl = reader.result;
        resolve(dataUrl);
      }
    };
    reader.onerror = () => {
      reader.abort();
      reject(new Error(reader.error));
    };
    reader.readAsDataURL(blob);
  });
}

/** Image used when requiring a placeholder */
export const placeholderImg = require('@/assets/img/square-picture-placeholder.png');

/** All possible values of the virtual path name */
export const ACCEPTED_PATHNAMES = [
  'informations/',
  'actions/',
  'problems/',
  'causes/',
  'gwComments/',
  'widgets/',
  'gwClItems/',
  'gwClItemComments/',
];

/** All possible values of a file type */
export const ACCEPTED_DATA_TYPES = [
  'information',
  'action',
  'problem',
  'cause',
  'gwComment',
  'widget',
  'gwClItemComment',
];

export default {
  FILE_TYPE_ACCEPTED,
  RE_IMAGE_TYPE,
  FILE_TYPE_PDF,
  FILE_TYPE_DOC,
  FILE_TYPE_DOCX,
  FILE_TYPE_ODT,
  FILE_TYPE_XLS,
  FILE_TYPE_XLSX,
  FILE_TYPE_ODS,
  FILE_TYPE_PPT,
  FILE_TYPE_PPTX,
  FILE_TYPE_ODP,
  getFileIconInfo,
  rotateCanvasImage,
  handlePictureOrientation,
  handlePictureDimension,
  blobToDataURL,
  placeholderImg,
  ACCEPTED_PATHNAMES,
  ACCEPTED_DATA_TYPES,
};
